import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Phone, GraduationCap, MapPin, User, Calendar, Loader2, X } from "lucide-react";
import profileicon from "../assets/profile.png";
import attendaceIcon from "../assets/attendanceIcon.png";
import payoutsIcon from "../assets/payoutsIcon.png";
import referralIcon from "../assets/referralIcon.png";
import resumeIcon from "../assets/resumeIcon.png";
import bankDetailsIcon from "../assets/bankDetails.png";
import agreementIcon from "../assets/agreementIcon.png";
import pendingBatch from '../assets/pendingBatch.png';
import verifiedBatch from '../assets/verifiedBatch.png';
import notverifiedBatch from '../assets/not_verified.svg'

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PaymentHistoryIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="6" width="26" height="30" rx="3" stroke="#6B4EFF" strokeWidth="2" fill="none"/>
    <circle cx="32" cy="32" r="8" fill="#f0f0f5" stroke="#6B4EFF" strokeWidth="2"/>
    <line x1="32" y1="28" x2="32" y2="32" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round"/>
    <line x1="32" y1="32" x2="35" y2="35" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round"/>
    <line x1="11" y1="14" x2="21" y2="14" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round"/>
    <line x1="11" y1="19" x2="21" y2="19" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round"/>
    <line x1="11" y1="24" x2="18" y2="24" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const IncentivePayoutIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="12" width="32" height="22" rx="3" stroke="#6B4EFF" strokeWidth="2" fill="none"/>
    <circle cx="34" cy="28" r="5" fill="#f0f0f5" stroke="#6B4EFF" strokeWidth="2"/>
    <circle cx="34" cy="28" r="2" fill="#6B4EFF"/>
    <line x1="6" y1="18" x2="38" y2="18" stroke="#6B4EFF" strokeWidth="2"/>
    <line x1="10" y1="24" x2="16" y2="24" stroke="#6B4EFF" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const ReferralPayoutIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="22" cy="30" r="4" stroke="#6B4EFF" strokeWidth="2" fill="none"/>
    <circle cx="10" cy="30" r="4" stroke="#6B4EFF" strokeWidth="2" fill="none"/>
    <circle cx="34" cy="30" r="4" stroke="#6B4EFF" strokeWidth="2" fill="none"/>
    <circle cx="22" cy="14" r="4" stroke="#6B4EFF" strokeWidth="2" fill="none"/>
    <line x1="22" y1="18" x2="22" y2="26" stroke="#6B4EFF" strokeWidth="2"/>
    <line x1="22" y1="26" x2="10" y2="26" stroke="#6B4EFF" strokeWidth="2"/>
    <line x1="22" y1="26" x2="34" y2="26" stroke="#6B4EFF" strokeWidth="2"/>
    <line x1="10" y1="26" x2="10" y2="26" stroke="#6B4EFF" strokeWidth="2"/>
  </svg>
);

const maskAccountNumber = (accountNumber) => {
  if (!accountNumber) return "Not Available";
  const str = String(accountNumber);
  if (str.length <= 4) return str;
  return "XXXX" + str.slice(-4);
};

// Read-only Bank Details modal — matches the reference design, no edit action.
const BankDetailsModal = ({ bankDetails, onClose }) => {
  const rows = [
    { label: "Account Holder", value: bankDetails?.bankName },
    { label: "Account Number", value: maskAccountNumber(bankDetails?.accountNumber) },
    { label: "IFSC Code", value: bankDetails?.IFSC },
    { label: "Bank Name", value: bankDetails?.bank_name },
    { label: "Branch", value: bankDetails?.branchName },
    { label: "City", value: bankDetails?.city },
    { label: "State", value: bankDetails?.state },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
      <div
        className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden"
        style={{ fontFamily: "'Montserrat', sans-serif" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 bg-[#F2F2FD]">
          <h2 className="text-lg font-bold text-gray-900">Bank Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="h-px bg-gray-100" />

        <div className="px-6 py-5">
          {bankDetails ? (
            <div>
              <div className="space-y-3 mb-5">
                {rows.map(({ label, value }, i, arr) => (
                  <div
                    key={label}
                    className={`flex justify-between items-center py-2 ${
                      i < arr.length - 1 ? "border-b border-gray-100" : ""
                    }`}
                  >
                    <span className="text-sm text-gray-500 shrink-0">{label}</span>
                    <span className="text-sm font-semibold text-gray-800 text-right min-w-0 ml-4 max-w-[55%] break-words">
                      {value || "—"}
                    </span>
                  </div>
                ))}
              </div>
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center py-4">
              <p className="text-gray-500 text-sm mb-5">No bank details added yet</p>
              <button
                onClick={onClose}
                className="px-8 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const WorkerProfilePage = () => {
  const { id: workerId } = useParams();
  const navigate = useNavigate();

  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBankModal, setShowBankModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  const fetchWorkerDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem("admin_auth_token");
      if (!token) {
        navigate("/lkart/admin");
        return;
      }
      if (!workerId) {
        setError("No worker specified");
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/worker-details`,
        { workerId },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        setWorker(response.data.data);
      } else {
        setError(response.data.message || "Failed to load worker");
      }
    } catch (err) {
      console.error("Failed to fetch worker details:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("superadmintoken");
        navigate("/lkart/admin");
      } else {
        setError(err.response?.data?.message || "Failed to load worker profile");
      }
    } finally {
      setLoading(false);
    }
  }, [workerId, navigate]);

  useEffect(() => {
    fetchWorkerDetails();
  }, [fetchWorkerDetails]);

const quickActions = [
  {
    icon: attendaceIcon,
    label: "Attendance",
    onClick: () => navigate(`/lkart/admin/worker-attendance/${workerId}`),
  },
  {
    icon: PaymentHistoryIcon,
    label: "Payment History",
    isComponent: true,
    onClick: () => navigate(`/lkart/admin/worker-payouts/${workerId}`),
  },
  {
    icon: IncentivePayoutIcon,
    label: "Incentive History",
    isComponent: true,
    onClick: () => navigate(`/lkart/admin/worker-incentive-payouts/${workerId}`),
  },
  {
    icon: ReferralPayoutIcon,
    label: "Referral History",
    isComponent: true,
    onClick: () => navigate(`/lkart/admin/worker-referral-payouts/${workerId}`),
  },
  {
    icon: PaymentHistoryIcon,
    label: "Payment Mode",
    isComponent: true,
    onClick: () => navigate(`/lkart/admin/payment-mode/${workerId}`),
  },
  {
    icon: bankDetailsIcon,
    label: "Bank Details",
    
    onClick: () => setShowBankModal(true),
  },
];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !worker) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 text-center">
        <p className="text-red-600">{error || "Worker not found"}</p>
      </div>
    );
  }

  const formatDob = (dob) => {
    if (!dob) return "Not Available";
    const d = new Date(dob);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  // Built as a plain variable (not inline JSX in an array) to avoid
  // editor/clipboard tools mangling a bare "<a" tag on its own line.
  let callButton = null;
  // if (worker.phone) {
  //   callButton = React.createElement(
  //     "a",
  //     {
  //       href: "tel:" + worker.phone,
  //       className:
  //         "text-[11px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full hover:bg-indigo-100 transition-colors flex-shrink-0",
  //     },
  //     "Call"
  //   );
  // }

  return (
    <div className="min-h-screen bg-gray-50 px-3 py-14 sm:px-6 sm:py-14">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-900 mb-5 sm:mb-6">
          Worker Profile
        </h1>

        {/* Profile + Skills Card */}
        <div className="bg-[#F6FBFF] rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-0">
            {/* Avatar + basic info */}
            <div className="md:col-span-2 md:pr-4 flex flex-col min-w-0">
             <div className="flex items-center justify-between gap-3 mb-4">
  <div className="flex items-center gap-3 min-w-0">
    <img
      src={worker.profilePhotoUrl || profileicon}
      alt="Profile"
      onClick={() => setShowPhotoModal(true)}
      className="w-14 h-14 rounded-full object-cover border-2 border-green-500 flex-shrink-0 cursor-pointer hover:opacity-90 transition"
    />
    <div className="min-w-0">
      <h2 className="text-base font-bold text-gray-900 truncate">
        {worker.name || "Worker"}
      </h2>
        <div className="flex items-center gap-2">
<img
  src={
    worker?.verification?.nameVerification?.status === "success"
      ? verifiedBatch
      : worker?.verification?.nameVerification?.status === "failed"
      ? notverifiedBatch
      : pendingBatch
  }
  alt={
    worker?.verification?.nameVerification?.status === "success"
      ? "Verified"
      : worker?.verification?.nameVerification?.status === "failed"
      ? "Not Verified"
      : "Pending"
  }
  className="h-6 w-auto object-contain"
/>
  </div>
      <p className="text-sm text-[#3b4a92] font-medium truncate">
        {worker.workerCode || "Not Available"}
      </p>
    </div>
  </div>

{worker.phone && (
  <a
    href={`tel:${worker.phone}`}
    className="flex-shrink-0 w-9 h-9 rounded-full bg-green-500 flex items-center justify-center shadow-sm"
    aria-label="Call worker"
  >
    <Phone className="w-4 h-4 text-white" fill="white" />
  </a>
)}
</div>

              <div className="grid grid-cols-2 gap-y-3 gap-x-2 text-sm text-[#3b4a92]">
                <div className="flex items-center gap-2 min-w-0">
                  <Phone className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{worker.phone || "Not Available"}</span>
                  {callButton}
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <GraduationCap className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{worker.highestQualification || "Not Available"}</span>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <User className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{worker.gender || "Not Available"}</span>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <Calendar className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{formatDob(worker.dob)}</span>
                </div>
              </div>
            </div>

            {/* Address + Skills */}
            <div className="md:col-span-3 md:pl-6 flex flex-col justify-center gap-4 text-[#3b4a92] border-t md:border-t-0 md:border-l border-gray-300 pt-4 md:pt-0 min-w-0">
              <div className="flex items-start gap-3 min-w-0">
                <MapPin className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div className="w-full min-w-0">
                  <p className="text-xs font-medium text-[#7a88b8]">Address</p>
                  <p className="text-sm font-medium text-gray-800 break-words leading-snug">
                    {worker.address || "Not Available"}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 min-w-0">
                <div className="h-5 w-5 flex-shrink-0 mt-0.5 flex items-center justify-center text-[#3b4a92]">
                  🛠️
                </div>
                <div className="w-full min-w-0">
                  <p className="text-xs font-medium text-[#7a88b8]">Skills</p>
                  <p className="text-sm font-medium text-gray-800 break-words leading-snug">
                    {worker.skills?.length > 0 ? worker.skills.join(", ") : "Not Available"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Card */}
        <div className="bg-[#F6FBFF] rounded-2xl border border-gray-200 shadow-sm p-4 sm:p-5 mb-4 flex items-center justify-between gap-3">
          <div className="flex gap-4 sm:gap-6 min-w-0">
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-[11px] text-gray-500 font-medium whitespace-nowrap">
                Shifts Hours
              </span>
              <span className="text-base font-bold text-[#101d75] truncate">
                {typeof worker.totalWorkingHours === "number"
                  ? worker.totalWorkingHours.toFixed(2)
                  : worker.totalWorkingHours ?? "0"}
              </span>
            </div>
            <div className="flex flex-col gap-1 border-l border-gray-300 pl-4 sm:pl-6 min-w-0">
              <span className="text-[11px] text-gray-500 font-medium whitespace-nowrap">
                Total Shifts
              </span>
              <span className="text-base font-bold text-[#101d75] truncate">
                {worker.totalShifts ?? 0}
              </span>
            </div>
            <div className="flex flex-col gap-1 border-l border-gray-300 pl-4 sm:pl-6 min-w-0">
              <span className="text-[11px] text-gray-500 font-medium whitespace-nowrap">
                Earnings
              </span>
              <span className="text-base font-bold text-[#101d75] truncate">
                ₹{worker.totalGrossAmount ?? 0}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-4">
          <h3 className="text-sm font-bold text-gray-800 mb-3 px-1">Quick Actions</h3>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-2">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
              {quickActions.map((action, i) => (
                <button
                  key={i}
                  onClick={action.onClick}
                  className="flex flex-col items-center gap-2.5 py-5 px-2 rounded-xl hover:bg-indigo-50/70 active:bg-indigo-50 transition-colors"
                >
<div className="w-11 h-11 rounded-xl bg-[#F6FBFF] border border-gray-200 flex items-center justify-center">
  {action.isComponent ? (
    <action.icon />
  ) : (
    <img src={action.icon} alt={action.label} className="w-6 h-6 object-contain" />
  )}
</div>
                  <span className="text-xs font-medium text-gray-700 text-center leading-tight">
                    {action.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bank Details Modal */}
      {showBankModal && (
        <BankDetailsModal
          bankDetails={worker.bankDetails}
          onClose={() => setShowBankModal(false)}
        />
      )}

        {showPhotoModal && (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={() => setShowPhotoModal(false)}
    >
      <div className="relative max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => setShowPhotoModal(false)}
          className="absolute -top-4 -right-4 w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-lg hover:bg-gray-100 transition"
          aria-label="Close"
        >
          <span className="text-xl leading-none text-gray-700">×</span>
        </button>
        <img
          src={worker.profilePhotoUrl || profileicon}
          alt="Profile large"
          className="w-full h-auto rounded-lg object-cover shadow-2xl"
        />
      </div>
    </div>
  )}
    </div>
  );
};

export default WorkerProfilePage;